import { useSyncExternalStore } from "react"

export const marketingLocales = [{ code: "en", label: "English", short: "EN" }] as const
export type MarketingLocale = (typeof marketingLocales)[number]["code"]

const storageKey = "resolto.locale.v1"
const listeners = new Set<() => void>()
let currentLocale: MarketingLocale = "en"

const messages: Record<string, string> = {
  "nav.product": "Product",
  "nav.solutions": "Solutions",
  "nav.industries": "Industries",
  "nav.pricing": "Pricing",
  "nav.signIn": "Sign in",
  "nav.createAccount": "Open demo",
  "nav.menu": "Open navigation",
  "nav.theme": "Change colour theme",
  "nav.language": "Change language",
  "hero.title": "Resolve more.|Interrupt developers less.",
  "hero.body":
    "Resolto turns customer reports into structured cases with evidence, ownership, governed action, and a complete audit trail.",
  "hero.primary": "Open demo",
  "hero.secondary": "Book a demo",
  "cta.secondary": "Book a demo",
  "footer.resources": "Resources",
  "footer.product": "Product",
  "footer.company": "Company",
  "footer.legal": "Legal",
  "footer.statement":
    "Resolve support work with evidence, accountable ownership, and governed automation.",
  "footer.rights": "All rights reserved.",
  "footer.cookies": "Cookie settings",
  "cookie.title": "Your privacy, under your control",
  "cookie.body":
    "Resolto uses necessary storage to keep this demo working. Optional analytics and marketing storage stay off until you choose otherwise.",
  "cookie.accept": "Accept all",
  "cookie.reject": "Reject optional",
  "cookie.settings": "Manage settings",
  "cookie.open": "Open cookie settings",
  "cookie.policy": "Privacy notice",
  "cookie.controls": "Privacy controls",
  "cookie.dialogTitle": "Choose how this site stores data",
  "cookie.close": "Close cookie settings",
  "cookie.intro":
    "Necessary storage is always active. Resolto does not load optional analytics or marketing tools unless you enable them.",
  "cookie.necessary": "Necessary",
  "cookie.necessaryBody": "Authentication, security, availability, and saved privacy choices.",
  "cookie.preferences": "Preferences",
  "cookie.preferencesBody": "Remembers language, theme, and interface settings.",
  "cookie.analytics": "Analytics",
  "cookie.analyticsBody":
    "Measures page performance and product interest from aggregated activity.",
  "cookie.marketing": "Marketing",
  "cookie.marketingBody": "Measures campaigns or personalises marketing through approved services.",
  "cookie.save": "Save choices",
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}

export function initializeMarketingLocale(): void {
  if (typeof document !== "undefined") {
    document.documentElement.lang = currentLocale
    document.documentElement.dir = "ltr"
  }
}

export function useMarketingLocale() {
  const locale = useSyncExternalStore(
    subscribe,
    () => currentLocale,
    () => "en" as const,
  )

  const updateLocale = (next: MarketingLocale) => {
    currentLocale = next
    window.localStorage.setItem(storageKey, next)
    document.documentElement.lang = next
    listeners.forEach((listener) => {
      listener()
    })
  }

  return {
    locale,
    updateLocale,
    t: (key: string) => messages[key] ?? key,
  } as const
}
